import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, MapPin, Plus, Check, ShieldCheck, BadgePercent, X, Tag, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import api, { formatApiError, handleActError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { AddressFields } from "@/components/AddressFields";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReturnVideoNotice } from "@/components/ReturnVideoNotice";
import { MobileVerifyCard } from "@/pages/VerifyMobile";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BLANK = { name: "", phone: "", line1: "", line2: "", city: "", state: "", district: "", pincode: "", country: "", gstin: "", is_default: false };

export default function CheckoutPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const unverified = !!(user && user.email_verified === false);
  const [cart, setCart] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [paying, setPaying] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [fulfilChoice, setFulfilChoice] = useState({});
  const [deliveryPrefs, setDeliveryPrefs] = useState({});
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [mobileReq, setMobileReq] = useState(null);

  const loadCart = useCallback(() => {
    return api.get("/marketplace/cart").then((r) => { setCart(r.data); return r.data; });
  }, []);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplying(true);
    try {
      const r = await api.post("/marketplace/cart/apply-coupon", { code: couponInput.trim() });
      setCart((c) => ({ ...c, ...r.data }));
      toast.success(`Coupon ${r.data.coupon?.code} applied — you saved ₹${r.data.total_discount}`);
      setCouponInput("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const removeCoupon = async () => {
    try { await api.delete("/marketplace/cart/coupon"); await loadCart(); toast.success("Coupon removed"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const loadAddr = useCallback(() => {
    api.get("/marketplace/addresses").then((r) => {
      const list = r.data.addresses || []; setAddresses(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) setSelected(def.address_id);
      setAdding(list.length === 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/marketplace/cart").then((r) => { setCart(r.data); if (!(r.data.items || []).length) nav("/app/cart"); }).catch(() => nav("/app/cart"));
    loadAddr();
    api.get("/control/public/feature-flags").then((r) => setOtpRequired(!!r.data?.flags?.checkout_confirm_otp)).catch(() => {});
    loadMobileReq();
  }, [loadAddr, nav]);

  const loadMobileReq = () => api.get("/auth/mobile/requirement").then((r) => setMobileReq(r.data)).catch(() => {});
  // A verified mobile is required for every checkout (all transactional actions behave consistently).
  const mobileBlocked = mobileReq && mobileReq.verified === false;

  const sendOtp = async () => {
    setSendingOtp(true);
    try {
      await api.post("/marketplace/checkout/send-otp");
      setOtpSent(true);
      toast.success("Confirmation code sent", { description: `Check your email${user?.email ? ` (${user.email})` : ""}.` });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSendingOtp(false); }
  };

  const saveAddress = async () => {
    if (!form.name || !form.line1) { toast.error("Name and address are required"); return; }
    try { const r = await api.post("/marketplace/addresses", form); toast.success("Address saved"); setForm(BLANK); setAdding(false); setSelected(r.data.address.address_id); loadAddr(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const resendVerify = async () => {
    setResending(true);
    try { await api.post("/auth/resend-verification", { email: user.email, app_origin: window.location.origin }); toast.success("Verification email sent — check your inbox."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setResending(false); }
  };

  const loadRzp = () => new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
  });

  const pay = async () => {
    if (unverified) { toast.error("Please verify your email before placing an order."); return; }
    if (!selected) { toast.error("Please select a delivery address"); return; }
    if (mobileBlocked) { toast.error("Please verify your mobile number to place this order."); return; }
    if (otpRequired && !otpSent) { await sendOtp(); return; }
    if (otpRequired && otpCode.trim().length < 4) { toast.error("Enter the confirmation code we sent you."); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/marketplace/checkout/order", { address_id: selected, fulfilment: fulfilChoice, delivery_prefs: deliveryPrefs, otp: otpRequired ? otpCode.trim() : undefined });
      if (!data.configured) { toast.error(data.message, { description: "The marketplace payment gateway is not configured." }); setPaying(false); return; }
      const ok = await loadRzp();
      if (!ok) { toast.error("Could not load the payment gateway. Check your connection."); setPaying(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: data.currency,
        name: "Suffle Marketplace", description: `Order · ₹${data.total}`,
        prefill: { name: data.customer_name || "", email: data.customer_email || "" },
        theme: { color: "#2563eb" },
        handler: async (resp) => {
          try {
            const v = await api.post("/marketplace/checkout/verify", resp);
            window.dispatchEvent(new Event("cart-updated"));
            window.dispatchEvent(new Event("account-updated"));
            toast.success("Payment successful! Your order is placed.");
            nav("/app/shop-orders");
          } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Verification failed"); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", (r) => { toast.error("Payment failed", { description: r?.error?.description }); setPaying(false); });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setPaying(false); }
  };

  if (!cart) return null;

  return (
    <div data-testid="checkout-page">
      <PageHeader title="Checkout" icon={CreditCard} subtitle="Your order total is recalculated securely on our servers before payment." />
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="address-section">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><MapPin className="h-4 w-4 text-blue-500" /> Delivery Address</div>
            <div className="space-y-2">
              {addresses.map((a) => (
                <button key={a.address_id} onClick={() => setSelected(a.address_id)} data-testid={`address-${a.address_id}`}
                  className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left ${selected === a.address_id ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
                  <div className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border ${selected === a.address_id ? "border-blue-500 bg-blue-500" : "border-slate-300"}`}>{selected === a.address_id && <Check className="h-3 w-3 text-white" />}</div>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-800">{a.name} {a.phone && <span className="font-normal text-slate-500">· {a.phone}</span>}</p>
                    <p className="text-slate-600">{[a.line1, a.line2, a.district, a.city, a.state, a.pincode].filter(Boolean).join(", ")}</p>
                  </div>
                </button>
              ))}
            </div>
            {adding ? (
              <div className="mt-3 rounded-xl border border-slate-200 p-3" data-testid="address-form">
                <AddressFields value={form} onChange={setForm} showGstin />
                <button onClick={saveAddress} data-testid="addr-save" className="mt-3 w-full rounded-lg brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Save address</button>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} data-testid="add-address-btn" className="mt-3 inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600"><Plus className="h-3.5 w-3.5" /> Add a new address</button>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="checkout-items">
            <div className="mb-2 text-sm font-semibold text-slate-700">Items ({cart.count})</div>
            {cart.items.map((it, ci) => (
              <div key={`${it.product_id}-${it.variant_vid || ""}-${ci}`} className="flex justify-between border-t border-slate-100 py-2 text-sm first:border-t-0">
                <span className="text-slate-700">{it.name} × {it.qty}{it.mrp > it.price && <span className="ml-1.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`checkout-discount-${it.product_id}`}>{it.discount_pct || Math.round((it.mrp - it.price) / it.mrp * 100)}% off</span>}{it.delivery_estimate && <span className="mt-0.5 block text-[11px] font-medium text-slate-500" data-testid={`checkout-delivery-${it.product_id}`}>🚚 {it.delivery_estimate}</span>}</span>
                <span className="whitespace-nowrap"><span className="font-semibold text-slate-900">₹{it.line_total}</span>{it.mrp > it.price && <span className="ml-1.5 text-xs text-slate-400 line-through">₹{Math.round(it.mrp * it.qty)}</span>}</span>
              </div>
            ))}
          </div>

          {(cart.groups || []).some((g) => g.advance) && (
            <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="checkout-advance">
              <div className="mb-1 text-sm font-semibold text-violet-800">Choose your delivery date</div>
              <p className="mb-2 text-xs text-violet-600">Some items are made to order. Pick when you'd like them delivered.</p>
              {(cart.groups || []).filter((g) => g.advance).map((g) => {
                const sid = g.seller_id; const pref = deliveryPrefs[sid] || {};
                return (
                  <div key={sid} className="border-t border-violet-100 py-2 first:border-t-0" data-testid={`advance-group-${sid}`}>
                    <p className="text-xs font-medium text-slate-500">{g.seller?.name || "Seller"} · made to order (ready in ~{g.advance_lead_days} day{g.advance_lead_days === 1 ? "" : "s"})</p>
                    <div className="mt-1 flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-slate-500">Delivery date</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button data-testid={`advance-date-${sid}`} className="inline-flex h-9 w-48 items-center gap-2 rounded-lg border border-slate-200 px-3 text-left text-sm text-slate-700 hover:border-slate-300">
                              <CalendarIcon className="h-4 w-4 text-slate-400" />
                              {pref.date ? format(new Date(pref.date), "dd-MM-yyyy") : <span className="text-slate-400">Pick a date</span>}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={pref.date ? new Date(pref.date) : undefined}
                              onSelect={(d) => d && setDeliveryPrefs((m) => ({ ...m, [sid]: { ...m[sid], date: format(d, "yyyy-MM-dd") } }))}
                              disabled={{ before: new Date(g.advance_min_date) }} initialFocus />
                          </PopoverContent>
                        </Popover>
                      </div>
                      {(g.advance_slots || []).length > 0 && (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-slate-500">Time slot</span>
                          <Select value={pref.slot || ""} onValueChange={(v) => setDeliveryPrefs((m) => ({ ...m, [sid]: { ...m[sid], slot: v } }))}>
                            <SelectTrigger data-testid={`advance-slot-${sid}`} className="h-9 w-56 text-sm"><SelectValue placeholder="Any time" /></SelectTrigger>
                            <SelectContent>
                              {g.advance_slots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {(cart.groups || []).some((g) => g.pickup_available) && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="checkout-fulfilment">
              <div className="mb-2 text-sm font-semibold text-slate-700">Delivery or pickup</div>
              {(cart.groups || []).filter((g) => g.pickup_available).map((g) => {
                const sid = g.seller_id; const choice = fulfilChoice[sid] || "delivery";
                return (
                  <div key={sid} className="border-t border-slate-100 py-2 first:border-t-0" data-testid={`fulfil-group-${sid}`}>
                    <p className="text-xs font-medium text-slate-500">{g.seller?.name || "Seller"}</p>
                    <div className="mt-1 flex gap-2">
                      {[["delivery", "Home delivery"], ["pickup", "Store pickup"]].map(([v, lbl]) => (
                        <button key={v} onClick={() => setFulfilChoice((m) => ({ ...m, [sid]: v }))} data-testid={`fulfil-${sid}-${v}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${choice === v ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600"}`}>{lbl}</button>
                      ))}
                    </div>
                    {choice === "pickup" && <p className="mt-1 text-[11px] text-emerald-600" data-testid={`fulfil-${sid}-note`}>You'll get the pickup address &amp; a Pickup OTP by email after payment.</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5" data-testid="checkout-summary">
          <h3 className="font-semibold text-slate-800">Payment</h3>

          {unverified && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="checkout-verify-email">
              <p className="font-semibold text-amber-800">Verify your email to place this order</p>
              <p className="mt-1 text-amber-700">We sent a verification link to <b>{user.email}</b>. Please confirm it, then refresh to continue to payment.</p>
              <button onClick={resendVerify} disabled={resending} data-testid="resend-verify-btn" className="mt-2 rounded-full bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50">{resending ? "Sending…" : "Resend verification email"}</button>
            </div>
          )}

          <div className="mt-3" data-testid="coupon-box">
            {cart.coupon?.applied ? (
              <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid="coupon-applied">
                <span className="flex items-center gap-1.5 font-medium text-emerald-700"><BadgePercent className="h-4 w-4" /> {cart.coupon.code} applied</span>
                <button onClick={removeCoupon} data-testid="remove-coupon-btn" className="text-emerald-600 hover:text-emerald-800"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="flex flex-1 items-center gap-1.5 rounded-lg border border-slate-200 px-2">
                  <Tag className="h-3.5 w-3.5 text-slate-400" />
                  <input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input" className="w-full py-2 text-sm outline-none" />
                </div>
                <button onClick={applyCoupon} disabled={applying} data-testid="apply-coupon-btn" className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-600 disabled:opacity-50">{applying ? "…" : "Apply"}</button>
              </div>
            )}
          </div>

          <div className="mt-3 flex justify-between text-sm text-slate-600"><span>Subtotal</span><span>₹{cart.subtotal}</span></div>
          {cart.total_discount > 0 && (
            <div className="mt-1 flex justify-between text-sm text-emerald-600" data-testid="checkout-discount"><span>Discounts</span><span>− ₹{cart.total_discount}</span></div>
          )}
          {(cart.applied_promotions || []).map((p) => (
            <div key={p.promo_id} className="mt-1 flex items-center gap-1.5 text-xs text-emerald-700"><BadgePercent className="h-3 w-3" /> {p.name}{p.code ? ` (${p.code})` : ""}</div>
          ))}
          <div className="mt-1 flex justify-between text-sm text-slate-600"><span>Delivery</span><span data-testid="checkout-shipping" className={cart.shipping_total > 0 ? "" : "text-emerald-600"}>{cart.shipping_total > 0 ? `₹${cart.shipping_total}` : "Free"}</span></div>
          <div className="mt-3 flex justify-between border-t border-slate-100 pt-3 text-base font-bold text-slate-900"><span>Total payable</span><span data-testid="checkout-total">₹{cart.payable_total ?? cart.final_total ?? cart.subtotal}</span></div>
          <ReturnVideoNotice where="checkout" className="mt-4" />
          {mobileBlocked && (
            <div className="mt-4" data-testid="checkout-mobile-gate">
              <p className="mb-2 text-xs font-semibold text-amber-700">Verify your mobile number to place this order.</p>
              <MobileVerifyCard onVerified={loadMobileReq} />
            </div>
          )}
          {otpRequired && otpSent && (
            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/50 p-3" data-testid="checkout-otp-box">
              <p className="text-xs font-semibold text-blue-800">Confirm your order</p>
              <p className="mb-2 text-[11px] text-slate-500">Enter the 6-digit code we sent to {user?.email ? <b>{user.email}</b> : "your email"}.</p>
              <div className="flex items-center gap-2">
                <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="6-digit code" data-testid="checkout-otp-input" className="h-9 w-40" />
                <button onClick={sendOtp} disabled={sendingOtp} data-testid="checkout-otp-resend" className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300 disabled:opacity-50">{sendingOtp ? "Sending…" : "Resend"}</button>
              </div>
            </div>
          )}
          <button onClick={pay} disabled={paying || sendingOtp || !selected || unverified || mobileBlocked} data-testid="pay-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full brand-gradient-bg py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50">
            {paying ? "Processing…" : unverified ? "Verify email to pay" : sendingOtp ? "Sending code…" : (otpRequired && !otpSent) ? "Send confirmation code" : (otpRequired && otpSent) ? `Confirm & Pay ₹${cart.payable_total ?? cart.final_total ?? cart.subtotal}` : `Pay ₹${cart.payable_total ?? cart.final_total ?? cart.subtotal}`}
          </button>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Secured by Razorpay. Your total is recalculated on our servers before payment.</p>
        </div>
      </div>
    </div>
  );
}
